// Mock Gemini AI service for portfolio demo
const mockGeminiService = {
  generateLegalDocument: async (prompt: string): Promise<string> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Generate a realistic contract based on the prompt
    const contractType = prompt.toLowerCase().includes('internship') ? 'internship' :
                        prompt.toLowerCase().includes('employment') ? 'employment' :
                        prompt.toLowerCase().includes('nda') ? 'nda' :
                        prompt.toLowerCase().includes('service') ? 'service' : 'general';
    
    return generateMockContract(contractType, prompt);
  },

  analyzeDocument: async (text: string): Promise<any> => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      summary: "This document appears to be a standard contract with typical clauses for this type of agreement.",
      keyPoints: [
        "Clear definition of parties involved",
        "Specific terms and conditions outlined",
        "Payment terms clearly stated",
        "Termination clauses included"
      ],
      risks: [
        "Consider adding a dispute resolution clause",
        "Intellectual property rights could be more specific"
      ],
      score: 85
    };
  },

  improveClause: async (clause: string): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return `IMPROVED VERSION:\n\n${clause}\n\nADDITIONAL CONSIDERATIONS:\n- Added clarity on specific terms\n- Included protection for both parties\n- Ensured legal compliance`;
  }
};

function generateMockContract(type: string, prompt: string): string {
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  const contracts = {
    internship: `INTERNSHIP AGREEMENT

This Internship Agreement ("Agreement") is entered into on ${date}, by and between:

COMPANY: [Company Name]
Address: [Company Address]
("Company")

AND

INTERN: [Intern Name]
Address: [Intern Address]
("Intern")

1. INTERNSHIP POSITION
The Company agrees to provide the Intern with an internship position in Software Development, commencing on [Start Date] and ending on [End Date].

2. DUTIES AND RESPONSIBILITIES
The Intern agrees to perform the following duties:
- Assist in software development projects
- Participate in code reviews and team meetings
- Learn and apply best practices in software engineering
- Complete assigned tasks within specified timeframes

3. COMPENSATION
This is an [paid/unpaid] internship position. If paid, the Intern will receive [compensation details].

4. WORK SCHEDULE
The Intern will work [number] hours per week, with a flexible schedule to be agreed upon with the supervisor.

5. CONFIDENTIALITY
The Intern agrees to maintain confidentiality of all proprietary information and trade secrets of the Company.

6. INTELLECTUAL PROPERTY
Any work product created by the Intern during the internship shall be the property of the Company.

7. TERMINATION
Either party may terminate this Agreement with [notice period] written notice.

8. LEARNING OBJECTIVES
The Company will provide mentorship and learning opportunities in:
- Modern software development practices
- Team collaboration and project management
- Professional workplace skills

9. EVALUATION
The Intern's performance will be evaluated at the end of the internship period.

10. GOVERNING LAW
This Agreement shall be governed by the laws of [Jurisdiction].

AGREED AND ACCEPTED:

Company Representative: ___________________ Date: ___________
Signature

Intern: ___________________ Date: ___________
Signature

---
Generated by Contract AI - Demo Version`,

    employment: `EMPLOYMENT AGREEMENT

This Employment Agreement ("Agreement") is made on ${date}, between:

EMPLOYER: [Company Name]
("Employer")

AND

EMPLOYEE: [Employee Name]
("Employee")

1. POSITION AND DUTIES
The Employee is hired as [Position Title] and will perform duties including but not limited to those outlined in the attached job description.

2. COMPENSATION
The Employee will receive an annual salary of $[Amount], payable in accordance with the Company's standard payroll schedule.

3. BENEFITS
The Employee is entitled to:
- Health insurance
- Paid time off (PTO)
- Retirement plan contributions
- Other benefits as per company policy

4. WORK SCHEDULE
Standard work hours are [hours] per week, with flexibility as required by the position.

5. CONFIDENTIALITY AND NON-DISCLOSURE
The Employee agrees to maintain strict confidentiality of all proprietary information.

6. INTELLECTUAL PROPERTY
All work products created during employment belong to the Employer.

7. TERMINATION
Either party may terminate this agreement with [notice period] written notice.

8. NON-COMPETE CLAUSE
The Employee agrees not to engage in competing business activities for [period] after termination.

9. GOVERNING LAW
This Agreement is governed by the laws of [Jurisdiction].

SIGNATURES:

Employer: ___________________ Date: ___________

Employee: ___________________ Date: ___________

---
Generated by Contract AI - Demo Version`,

    nda: `NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into on ${date}, by and between:

DISCLOSING PARTY: [Party Name]
("Disclosing Party")

AND

RECEIVING PARTY: [Party Name]
("Receiving Party")

1. DEFINITION OF CONFIDENTIAL INFORMATION
"Confidential Information" means any data or information that is proprietary to the Disclosing Party and not generally known to the public.

2. OBLIGATIONS OF RECEIVING PARTY
The Receiving Party agrees to:
- Hold all Confidential Information in strict confidence
- Not disclose Confidential Information to third parties
- Use Confidential Information only for the agreed purpose

3. EXCLUSIONS
This Agreement does not apply to information that:
- Is or becomes publicly available
- Was known prior to disclosure
- Is independently developed

4. TERM
This Agreement shall remain in effect for [period] from the date of signing.

5. RETURN OF MATERIALS
Upon termination, all Confidential Information must be returned or destroyed.

6. REMEDIES
The parties acknowledge that breach may cause irreparable harm and agree to equitable relief.

7. GOVERNING LAW
This Agreement is governed by the laws of [Jurisdiction].

AGREED:

Disclosing Party: ___________________ Date: ___________

Receiving Party: ___________________ Date: ___________

---
Generated by Contract AI - Demo Version`,

    service: `SERVICE AGREEMENT

This Service Agreement ("Agreement") is made on ${date}, between:

SERVICE PROVIDER: [Provider Name]
("Provider")

AND

CLIENT: [Client Name]
("Client")

1. SERVICES
The Provider agrees to provide the following services:
${prompt}

2. TERM
This Agreement begins on [Start Date] and continues until [End Date] or completion of services.

3. COMPENSATION
The Client agrees to pay $[Amount] for the services, payable according to the following schedule:
- [Payment terms]

4. DELIVERABLES
The Provider will deliver:
- [List of deliverables]

5. TIMELINE
Services will be completed according to the agreed timeline.

6. INTELLECTUAL PROPERTY
Ownership of work product will be transferred to the Client upon full payment.

7. WARRANTIES
The Provider warrants that services will be performed in a professional manner.

8. LIMITATION OF LIABILITY
Neither party shall be liable for indirect or consequential damages.

9. TERMINATION
Either party may terminate with [notice period] written notice.

10. GOVERNING LAW
This Agreement is governed by the laws of [Jurisdiction].

SIGNATURES:

Provider: ___________________ Date: ___________

Client: ___________________ Date: ___________

---
Generated by Contract AI - Demo Version`,

    general: `CONTRACT AGREEMENT

This Agreement is made on ${date}, between the parties described below.

PURPOSE:
${prompt}

TERMS AND CONDITIONS:

1. PARTIES
This Agreement is between [Party A] and [Party B].

2. SCOPE OF WORK
The parties agree to the following terms regarding: ${prompt}

3. DURATION
This Agreement shall be effective from [Start Date] to [End Date].

4. PAYMENT TERMS
Payment shall be made according to the agreed schedule.

5. RESPONSIBILITIES
Each party agrees to fulfill their respective obligations as outlined.

6. CONFIDENTIALITY
Both parties agree to maintain confidentiality of sensitive information.

7. TERMINATION
This Agreement may be terminated by either party with proper notice.

8. DISPUTE RESOLUTION
Any disputes shall be resolved through mediation or arbitration.

9. AMENDMENTS
This Agreement may be amended only in writing signed by both parties.

10. GOVERNING LAW
This Agreement is governed by applicable laws.

SIGNATURES:

Party A: ___________________ Date: ___________

Party B: ___________________ Date: ___________

---
Generated by Contract AI - Demo Version
For demonstration purposes only`
  };

  return contracts[type as keyof typeof contracts] || contracts.general;
}

export default mockGeminiService;

